/**
 * CrmMentionChip
 *
 * Renders a CRM mention (lead/contact/company) as a compact chip, similar to
 * Cursor's attachment chips. Used in chat input "attached" row and in message markdown.
 */

"use client"

import { cn } from "@/lib/utils"
import { IconUser, IconBuilding, IconUsers } from "@tabler/icons-react"

export type CrmMentionType = "lead" | "contact" | "company"

export interface CrmMentionChipProps {
  type: CrmMentionType
  label: string
  id?: string
  /** Smaller style for inline in messages */
  compact?: boolean
  className?: string
}

const typeConfig: Record<
  CrmMentionType,
  { icon: typeof IconUser; label: string; bgClass: string }
> = {
  lead: {
    icon: IconUser,
    label: "Lead",
    bgClass: "bg-primary/10 text-primary border-primary/20"
  },
  contact: {
    icon: IconUsers,
    label: "Contact",
    bgClass: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
  },
  company: {
    icon: IconBuilding,
    label: "Company",
    bgClass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
  }
}

export function CrmMentionChip({
  type,
  label,
  id,
  compact = false,
  className
}: CrmMentionChipProps) {
  const config = typeConfig[type]
  const Icon = config.icon

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border font-medium no-underline",
        config.bgClass,
        compact ? "px-1.5 py-0.5 text-xs" : "px-2 py-1 text-xs",
        className
      )}
      title={id ? `${config.label}: ${label} (${id})` : `${config.label}: ${label}`}
    >
      <Icon className={compact ? "size-3 shrink-0" : "size-3.5 shrink-0"} />
      <span className="max-w-[140px] truncate">{label}</span>
    </span>
  )
}

/** Parse @[type: label](type:id) tokens from text */
export function parseCrmMentionsFromText(
  text: string
): { type: CrmMentionType; label: string; id: string }[] {
  const mentions: { type: CrmMentionType; label: string; id: string }[] = []
  const re = /@\[([^:]+):\s*([^\]]+)\]\(([^:]+):([^)]+)\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const type = m[3].trim().toLowerCase() as CrmMentionType
    if (type === "lead" || type === "contact" || type === "company") {
      mentions.push({
        type,
        label: m[2].trim(),
        id: m[4].trim()
      })
    }
  }
  return mentions
}

export type CrmInputSegment =
  | { kind: "text"; value: string }
  | { kind: "mention"; type: CrmMentionType; id: string; label: string }

/** Split input into text and mention segments for inline chip display */
export function splitInputIntoMentionSegments(text: string): CrmInputSegment[] {
  const segments: CrmInputSegment[] = []
  const re = /@\[([^:]+):\s*([^\]]+)\]\((lead|contact|company):([^)]+)\)/g
  let lastEnd = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const type = m[3].trim().toLowerCase() as CrmMentionType
    if (type !== "lead" && type !== "contact" && type !== "company") continue
    if (m.index > lastEnd) {
      segments.push({ kind: "text", value: text.slice(lastEnd, m.index) })
    }
    segments.push({
      kind: "mention",
      type,
      id: m[4].trim(),
      label: m[2].trim()
    })
    lastEnd = m.index + m[0].length
  }
  if (lastEnd < text.length) {
    segments.push({ kind: "text", value: text.slice(lastEnd) })
  }
  return segments
}
